import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Branded Snagging / Inspection PDF report — embeds before/after photos per snag.
 *
 * Usage:
 *   await generateInspectionPDF({ inspection, snags, company, attachmentsAuth });
 *
 * attachmentsAuth — bearer token used to fetch /api/files/{id} for image embedding.
 */

const THEME = {
  primary: [240, 180, 60],
  primaryDark: [200, 140, 40],
  dark: [15, 23, 42],
  body: [51, 65, 85],
  muted: [100, 116, 139],
  border: [226, 232, 240],
  bandBg: [254, 243, 199],
  rose: [225, 29, 72],
  amber: [217, 119, 6],
  emerald: [5, 150, 105],
  sky: [2, 132, 199],
};

const SEVERITY_COLOR = {
  critical: THEME.rose,
  major: THEME.amber,
  minor: THEME.sky,
};

function sanitize(t) {
  if (t == null) return '';
  return String(t)
    .replace(/[\u2018\u2019\u201A\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u2033]/g, '"')
    .replace(/[\u2013\u2014\u2010\u2011\u2012]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/\u00B7/g, '-')
    .replace(/\u2022/g, '*')
    .replace(/\u2122/g, '(TM)')
    .replace(/\u00A9/g, '(c)')
    .replace(/\u00AE/g, '(R)')
    .replace(/\u00A0/g, ' ')
    .replace(/[\u200B-\u200F\u2009\u200A\uFEFF]/g, '')
    .replace(/[^\x00-\x7E\u00A0-\u00FF]/g, '?');
}

function drawLetterhead(doc, company) {
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(...THEME.primary);
  doc.rect(0, 0, W, 90, 'F');
  let textX = 32;
  if (company?.logo_url) {
    try { doc.addImage(company.logo_url, 'PNG', 32, 18, 54, 54); textX = 100; } catch { /* ignore */ }
  }
  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(sanitize(company?.name || 'FM Flexi Pro'), textX, 38);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(60, 52, 30);
  const lines = [
    company?.address,
    [company?.contact_email, company?.contact_phone].filter(Boolean).join('  |  '),
  ].filter(Boolean);
  let y = 54;
  lines.forEach(l => { doc.text(sanitize(l), textX, y); y += 12; });
  doc.setDrawColor(...THEME.primaryDark);
  doc.setLineWidth(0.8);
  doc.line(0, 92, W, 92);
}

function drawFooter(doc, company) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(...THEME.border);
    doc.setLineWidth(0.5);
    doc.line(32, H - 48, W - 32, H - 48);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...THEME.muted);
    doc.text(sanitize(company?.name || 'FM Flexi Pro'), 32, H - 34);
    doc.text(sanitize('FM Flexi Pro (TM) by SKG Apps  -  Snagging Report'), W / 2, H - 34, { align: 'center' });
    doc.text(sanitize(`Page ${i} of ${total}`), W - 32, H - 34, { align: 'right' });
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(...THEME.primaryDark);
    doc.text(sanitize('SGK Apps, branding and a Product of VRV Technical & Technology Solutions FZ LLC'),
      W / 2, H - 18, { align: 'center' });
  }
}

function ensure(doc, y, needed, company) {
  const H = doc.internal.pageSize.getHeight();
  if (y + needed > H - 56) {
    doc.addPage();
    drawLetterhead(doc, company);
    return 110;
  }
  return y;
}

// Pre-fetch an image and convert to base64 data URL so jsPDF can embed it.
async function fetchAsDataURL(url, authToken) {
  try {
    const sep = url.includes('?') ? '&' : '?';
    const fetchUrl = authToken && url.startsWith('/api/files/') ? `${url}${sep}auth=${encodeURIComponent(authToken)}` : url;
    const res = await fetch(fetchUrl, { credentials: 'include' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

function detectFormat(dataUrl) {
  if (!dataUrl) return 'JPEG';
  if (dataUrl.startsWith('data:image/png')) return 'PNG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  return 'JPEG';
}

function drawSeverityBadge(doc, x, y, severity) {
  const color = SEVERITY_COLOR[severity] || THEME.muted;
  const label = (severity || 'minor').toUpperCase();
  doc.setFillColor(...color);
  doc.roundedRect(x, y - 9, 44, 13, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text(label, x + 22, y, { align: 'center' });
}

export async function generateInspectionPDF({ inspection, snags = [], company, attachmentsAuth, view = false, checklistItems = [] }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  // Resolve absolute URL for images served by /api/files/{id}
  const toAbsolute = (u) => (u && u.startsWith('/api/') ? `${apiBase}${u}` : u);

  drawLetterhead(doc, company);

  // ── Cover Title ──
  let y = 130;
  doc.setTextColor(...THEME.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(sanitize('SNAGGING / INSPECTION REPORT'), 32, y);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...THEME.muted);
  doc.text(sanitize(`# ${inspection.inspection_number || inspection.id}`), 32, y + 18);

  // Right side timestamp
  doc.setFontSize(9);
  doc.text(sanitize(`Generated: ${new Date().toLocaleString()}`), W - 32, 130, { align: 'right' });

  y += 36;
  doc.setDrawColor(...THEME.primary);
  doc.setLineWidth(2);
  doc.line(32, y, 80, y);
  y += 18;

  // ── Inspection Meta Card ──
  const metaRows = [
    ['Title', inspection.title || ''],
    ['Type', (inspection.inspection_type || '').replace(/_/g, ' ')],
    ['Building', inspection.building_name || '—'],
    ['Unit / Apartment', inspection.unit_number || '—'],
    ['Inspector', inspection.inspector_name || '—'],
    ['Inspection Date', inspection.inspection_date || '—'],
    ['Tenant Present', inspection.tenant_present || '—'],
    ['Contractor Present', inspection.contractor_present || '—'],
    ['Status', (inspection.status || '').toUpperCase()],
  ];
  autoTable(doc, {
    startY: y,
    head: [['Field', 'Value']],
    body: metaRows.map(r => [sanitize(r[0]), sanitize(r[1])]),
    styles: { font: 'helvetica', fontSize: 9.5, cellPadding: 6, textColor: THEME.body, lineColor: THEME.border, lineWidth: 0.4 },
    headStyles: { fillColor: THEME.primary, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 32, right: 32 },
  });
  y = doc.lastAutoTable.finalY + 14;

  // ── KPI Strip ──
  y = ensure(doc, y, 80, company);
  const currency = inspection.currency || 'AED';
  const totalCost = Number(inspection.total_cost || 0);
  const cardW = (W - 64 - 32) / 5;
  const stats = [
    { label: 'Total Snags', value: inspection.total_snags ?? snags.length, color: THEME.dark },
    { label: 'Open', value: inspection.open_snags ?? 0, color: THEME.amber },
    { label: 'Critical', value: inspection.critical_snags ?? 0, color: THEME.rose },
    { label: 'Closed', value: inspection.closed_snags ?? 0, color: THEME.emerald },
    { label: `Total Cost (${currency})`, value: totalCost.toLocaleString(), color: THEME.primary },
  ];
  stats.forEach((s, idx) => {
    const x = 32 + idx * (cardW + 8);
    doc.setDrawColor(...THEME.border);
    doc.setFillColor(252, 252, 252);
    doc.roundedRect(x, y, cardW, 60, 6, 6, 'FD');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...THEME.muted);
    doc.text(sanitize(s.label.toUpperCase()), x + 10, y + 18);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(...s.color);
    doc.text(String(s.value), x + 10, y + 48);
  });
  y += 80;

  if (inspection.summary) {
    y = ensure(doc, y, 60, company);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...THEME.primary);
    doc.text(sanitize('Inspector Summary'), 32, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    doc.setTextColor(...THEME.body);
    const lines = doc.splitTextToSize(sanitize(inspection.summary), W - 64);
    lines.forEach(line => { y = ensure(doc, y, 14, company); doc.text(line, 32, y); y += 14; });
    y += 6;
  }

  // ── Standard Checklist (tenant check-in/out & customisable) ──
  const checklistCostTotal = checklistItems.reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const tenantChargeTotal = checklistItems.filter(r => r.charged_to === 'tenant').reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const companyChargeTotal = checklistItems.filter(r => r.charged_to === 'company').reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const sharedChargeTotal = checklistItems.filter(r => r.charged_to === 'shared').reduce((s, r) => s + (Number(r.cost) || 0), 0);

  if (checklistItems.length) {
    y = ensure(doc, y, 60, company);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...THEME.primary);
    doc.text(sanitize(`Standard Checklist (${checklistItems.length})`), 32, y);
    y += 14;

    autoTable(doc, {
      startY: y,
      head: [['S.No', 'Details', 'Findings', 'Action Required', `Cost (${currency})`, 'Charged To', 'Remarks']],
      body: checklistItems.map((r, i) => [
        sanitize(String(r.sno || (i + 1))),
        sanitize(r.details || ''),
        sanitize(r.findings || ''),
        sanitize(r.action_required || ''),
        r.cost != null ? Number(r.cost).toLocaleString() : '-',
        sanitize((r.charged_to || 'na').toUpperCase()),
        sanitize(r.remarks || ''),
      ]),
      foot: [[
        { content: 'TOTALS', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: checklistCostTotal.toLocaleString(), styles: { fontStyle: 'bold', textColor: THEME.primary } },
        { content: `Tenant: ${tenantChargeTotal.toLocaleString()}  |  Company: ${companyChargeTotal.toLocaleString()}${sharedChargeTotal ? `  |  Shared: ${sharedChargeTotal.toLocaleString()}` : ''}`, colSpan: 2, styles: { fontStyle: 'bold', fontSize: 8 } },
      ]],
      styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 5, textColor: THEME.body, lineColor: THEME.border, lineWidth: 0.4 },
      headStyles: { fillColor: THEME.primary, textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [254, 243, 199], textColor: THEME.dark },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 30, halign: 'center' },
        4: { halign: 'right' },
        5: { halign: 'center' },
      },
      margin: { left: 32, right: 32 },
      didDrawPage: () => { drawLetterhead(doc, company); },
    });
    y = doc.lastAutoTable.finalY + 12;

    // Cost split callouts — three coloured cards (Tenant / Company / Shared)
    y = ensure(doc, y, 60, company);
    const boxW = (W - 64 - 16) / 3;
    const splitCards = [
      { label: 'CHARGED TO TENANT', amount: tenantChargeTotal, color: THEME.amber, tint: [254, 243, 199] },
      { label: 'BORNE BY COMPANY', amount: companyChargeTotal, color: THEME.sky, tint: [224, 242, 254] },
      { label: 'SHARED / OTHER', amount: sharedChargeTotal, color: THEME.muted, tint: [241, 245, 249] },
    ];
    splitCards.forEach((c, idx) => {
      const x = 32 + idx * (boxW + 8);
      doc.setFillColor(...c.tint);
      doc.setDrawColor(...c.color);
      doc.setLineWidth(0.6);
      doc.roundedRect(x, y, boxW, 44, 5, 5, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...c.color);
      doc.text(sanitize(c.label), x + 10, y + 14);
      doc.setFontSize(16);
      doc.setTextColor(...THEME.dark);
      doc.text(`${currency} ${Number(c.amount).toLocaleString()}`, x + 10, y + 34);
    });
    y += 60;
  }

  // ── Snag Summary Table (one row per snag) ──
  if (snags.length) {
    y = ensure(doc, y, 60, company);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...THEME.primary);
    doc.text(sanitize(`Snag List (${snags.length})`), 32, y);
    y += 14;

    autoTable(doc, {
      startY: y,
      head: [['#', 'Location', 'Category', 'Severity', 'Description', 'Status', 'Trade', `Cost (${currency})`]],
      body: snags.map(s => [
        sanitize(s.snag_number || ''),
        sanitize(s.location || ''),
        sanitize(s.category || ''),
        sanitize(s.severity || ''),
        sanitize((s.description || '').slice(0, 70)),
        sanitize(s.status || ''),
        sanitize(s.trade || ''),
        s.repair_cost != null ? Number(s.repair_cost).toLocaleString() : '-',
      ]),
      foot: [[
        { content: `TOTAL (${snags.length} snags)`, colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: totalCost.toLocaleString(), styles: { fontStyle: 'bold', textColor: THEME.primary } },
      ]],
      styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 5, textColor: THEME.body, lineColor: THEME.border, lineWidth: 0.4 },
      headStyles: { fillColor: THEME.primary, textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [248, 250, 252], textColor: THEME.dark },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 32, right: 32 },
      didDrawPage: () => { drawLetterhead(doc, company); },
    });
    y = doc.lastAutoTable.finalY + 18;
  }

  // ── Per-snag detail pages with embedded photos ──
  for (const s of snags) {
    doc.addPage();
    drawLetterhead(doc, company);
    let py = 110;

    // Snag header band
    doc.setFillColor(...THEME.bandBg);
    doc.roundedRect(32, py, W - 64, 36, 4, 4, 'F');
    doc.setTextColor(...THEME.dark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(sanitize(`${s.snag_number || 'Snag'} - ${s.location || ''}`), 42, py + 23);
    drawSeverityBadge(doc, W - 88, py + 24, s.severity);
    py += 50;

    // Meta two-column block
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...THEME.body);
    const left = [
      ['Category', s.category],
      ['Trade', s.trade || '-'],
      ['Status', (s.status || '').toUpperCase()],
    ];
    const right = [
      ['Assigned', s.assigned_to_name || '-'],
      ['Target Date', s.target_date || '-'],
      ['Rectified', s.rectified_at ? new Date(s.rectified_at).toLocaleDateString() : '-'],
      ['Repair Cost', s.repair_cost != null ? `${currency} ${Number(s.repair_cost).toLocaleString()}` : '-'],
    ];
    const colW = (W - 64) / 2;
    left.forEach((row, i) => {
      doc.setFont('helvetica', 'bold'); doc.text(sanitize(row[0] + ':'), 32, py + (i * 14));
      doc.setFont('helvetica', 'normal'); doc.text(sanitize(String(row[1] || '')), 100, py + (i * 14));
    });
    right.forEach((row, i) => {
      doc.setFont('helvetica', 'bold'); doc.text(sanitize(row[0] + ':'), 32 + colW, py + (i * 14));
      doc.setFont('helvetica', 'normal'); doc.text(sanitize(String(row[1] || '')), 32 + colW + 80, py + (i * 14));
    });
    py += left.length * 14 + 10;

    // Description box
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...THEME.primary);
    doc.text(sanitize('Description'), 32, py);
    py += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...THEME.body);
    const descLines = doc.splitTextToSize(sanitize(s.description || ''), W - 64);
    descLines.forEach(line => { py = ensure(doc, py, 14, company); doc.text(line, 32, py); py += 13; });
    py += 6;

    // Photos before / after
    const drawPhotoStrip = async (urls, label) => {
      if (!urls || !urls.length) return;
      py = ensure(doc, py, 130, company);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.setTextColor(...THEME.primary);
      doc.text(sanitize(label), 32, py);
      py += 10;
      const photoW = 150;
      const photoH = 110;
      const gap = 12;
      const perRow = Math.floor((W - 64 + gap) / (photoW + gap));
      let col = 0;
      let rowTop = py;
      for (const u of urls) {
        if (col === 0) py = ensure(doc, py, photoH + 8, company), rowTop = py;
        const absUrl = toAbsolute(u);
        const dataUrl = await fetchAsDataURL(absUrl, attachmentsAuth);
        if (dataUrl) {
          try {
            const fmt = detectFormat(dataUrl);
            doc.addImage(dataUrl, fmt, 32 + col * (photoW + gap), rowTop, photoW, photoH);
          } catch {
            doc.setDrawColor(...THEME.border);
            doc.rect(32 + col * (photoW + gap), rowTop, photoW, photoH);
          }
        } else {
          doc.setDrawColor(...THEME.border);
          doc.setFillColor(248, 250, 252);
          doc.rect(32 + col * (photoW + gap), rowTop, photoW, photoH, 'FD');
          doc.setFontSize(8); doc.setTextColor(...THEME.muted);
          doc.text(sanitize('[image unavailable]'), 32 + col * (photoW + gap) + 30, rowTop + photoH / 2);
        }
        col++;
        if (col >= perRow) { col = 0; py = rowTop + photoH + 8; }
      }
      if (col > 0) py = rowTop + photoH + 10;
    };

    await drawPhotoStrip(s.photos_before, 'Photos - BEFORE');
    await drawPhotoStrip(s.photos_after, 'Photos - AFTER');

    if (s.notes) {
      py = ensure(doc, py, 30, company);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.setTextColor(...THEME.primary);
      doc.text(sanitize('Notes'), 32, py);
      py += 12;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...THEME.body);
      const nLines = doc.splitTextToSize(sanitize(s.notes), W - 64);
      nLines.forEach(line => { py = ensure(doc, py, 14, company); doc.text(line, 32, py); py += 13; });
    }
  }

  // Signature block on a final page — Feb-18: enhanced with Final Agreed Cost
  // callout and dedicated Management / Tenant approval slots.
  doc.addPage();
  drawLetterhead(doc, company);
  let sy = 130;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...THEME.dark);
  doc.text(sanitize('Sign-off & Cost Agreement'), 32, sy);
  sy += 8;
  doc.setDrawColor(...THEME.primary);
  doc.setLineWidth(2);
  doc.line(32, sy, 80, sy);
  sy += 24;

  // Grand total = snag repair total + checklist total
  const snagCostTotal = snags.reduce((s, r) => s + (Number(r.repair_cost) || 0), 0);
  const grandTotal = snagCostTotal + checklistCostTotal;

  // Final Agreed Cost callout — bold branded card
  doc.setFillColor(...THEME.bandBg);
  doc.setDrawColor(...THEME.primaryDark);
  doc.setLineWidth(1);
  doc.roundedRect(32, sy, W - 64, 78, 8, 8, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...THEME.primaryDark);
  doc.text(sanitize('FINAL AGREED COST (AS PER MANAGEMENT)'), 44, sy + 22);

  // Use approved value if present; else fall back to computed grand total
  const approvedFinal = inspection.final_agreed_cost != null ? Number(inspection.final_agreed_cost) : grandTotal;
  doc.setFontSize(28);
  doc.setTextColor(...THEME.dark);
  doc.text(sanitize(`${currency} ${approvedFinal.toLocaleString()}`), 44, sy + 54);

  // Break-down on the right side of the callout
  const rightX = W - 220;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...THEME.body);
  const bd = [
    ['Snag Repair Costs', snagCostTotal],
    ['Checklist Items', checklistCostTotal],
    ['Charged to Tenant', tenantChargeTotal],
    ['Borne by Company', companyChargeTotal],
  ];
  bd.forEach((row, i) => {
    const yy = sy + 20 + i * 12;
    doc.text(sanitize(row[0]), rightX, yy);
    doc.setFont('helvetica', 'bold');
    doc.text(`${currency} ${Number(row[1]).toLocaleString()}`, W - 44, yy, { align: 'right' });
    doc.setFont('helvetica', 'normal');
  });

  // APPROVED stamp — green ribbon inside the callout, bottom-right
  if (inspection.cost_status === 'approved') {
    const stampX = W - 200;
    const stampY = sy + 68;
    doc.setDrawColor(...THEME.emerald);
    doc.setLineWidth(1.6);
    doc.setFillColor(240, 253, 244);
    doc.roundedRect(stampX, stampY - 20, 156, 24, 4, 4, 'FD');
    doc.setTextColor(...THEME.emerald);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(sanitize('APPROVED FINAL COST'), stampX + 78, stampY - 8, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...THEME.body);
    const approverName = inspection.cost_approved_by_name || 'Management';
    const approvedDate = inspection.cost_approved_at ? new Date(inspection.cost_approved_at).toLocaleDateString() : '';
    doc.text(sanitize(`by ${approverName} on ${approvedDate}`), stampX + 78, stampY, { align: 'center' });
  }

  sy += 96;

  // Revision trail — "Revised cost by (name) on (date)"
  if ((inspection.cost_revisions || []).length) {
    sy = ensure(doc, sy, 40, company);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...THEME.primaryDark);
    doc.text(sanitize('Revision History'), 32, sy);
    sy += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...THEME.body);
    (inspection.cost_revisions || []).forEach(rev => {
      sy = ensure(doc, sy, 18, company);
      const when = rev.revised_at ? new Date(rev.revised_at).toLocaleDateString() : '';
      const line = `Revised cost by ${rev.revised_by_name || 'Manager'} on ${when} - ${currency} ${Number(rev.old_cost || 0).toLocaleString()} -> ${currency} ${Number(rev.new_cost || 0).toLocaleString()}${rev.note ? ' (' + rev.note + ')' : ''}`;
      const lines = doc.splitTextToSize(sanitize(line), W - 64);
      lines.forEach(l => { sy = ensure(doc, sy, 12, company); doc.text('- ' + l, 32, sy); sy += 12; });
    });
    sy += 8;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...THEME.muted);
  const disclaimer = 'By signing below, both parties acknowledge that they have reviewed the findings, associated repair costs, and agree to the settlement amount stated above as the FINAL AGREED COST.';
  const dLines = doc.splitTextToSize(sanitize(disclaimer), W - 64);
  dLines.forEach(l => { sy = ensure(doc, sy, 12, company); doc.text(l, 32, sy); sy += 12; });
  sy += 10;

  // Signature grid — two columns × two rows
  const sigCols = [32, 32 + (W - 64) / 2 + 8];
  const sigW = (W - 64) / 2 - 8;
  const sigH = 100;
  const sigBlocks = [
    { label: 'INSPECTOR', name: inspection.inspector_name, subtitle: 'Facilities Management' },
    { label: 'TENANT', name: inspection.tenant_present, subtitle: 'Confirming vacation of the premises' },
    { label: 'APPROVED BY MANAGEMENT', name: '', subtitle: 'Facilities Manager / Property Owner' },
    { label: 'CONTRACTOR', name: inspection.contractor_present, subtitle: 'Optional — for repair scope' },
  ];
  sigBlocks.forEach((b, idx) => {
    const col = idx % 2;
    const rowIdx = Math.floor(idx / 2);
    if (col === 0 && rowIdx > 0) sy += sigH + 16;
    sy = ensure(doc, sy, sigH + 20, company);
    const x = sigCols[col];

    doc.setDrawColor(...THEME.border);
    doc.setLineWidth(0.4);
    doc.roundedRect(x, sy, sigW, sigH, 5, 5, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...THEME.primaryDark);
    doc.text(sanitize(b.label), x + 10, sy + 16);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...THEME.muted);
    doc.text(sanitize(b.subtitle), x + 10, sy + 30);

    // Name
    doc.setFontSize(10);
    doc.setTextColor(...THEME.body);
    doc.text(sanitize(`Name: ${b.name || '________________________'}`), x + 10, sy + 52);

    // Signature line
    doc.setDrawColor(...THEME.dark);
    doc.setLineWidth(0.5);
    doc.line(x + 10, sy + 78, x + sigW - 10, sy + 78);
    doc.setFontSize(8);
    doc.setTextColor(...THEME.muted);
    doc.text(sanitize('Signature & Date'), x + 10, sy + 90);
  });
  sy += sigH + 8;

  drawFooter(doc, company);

  const safeName = `Snagging_${inspection.inspection_number || inspection.id}_${new Date().toISOString().split('T')[0]}.pdf`;
  if (view) {
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) doc.save(safeName);
  } else {
    doc.save(safeName);
  }
}
